import React, { Fragment } from "react";
import {
  Popover,
  PopoverButton,
  PopoverPanel,
  Transition,
} from "@headlessui/react";
import { HiChevronDown } from "react-icons/hi";
import useHeaderDailyTargets from "../../hooks/useHeaderDailyTargets";
import { getAdminPanelRole } from "../../utils/adminPanelRole";

const ROLES_WITH_DAILY_TARGETS = new Set([
  "admin",
  "developer",
  "csr",
  "csr team lead",
  "chat support",
]);

export function canSeeDailyTargetsHeader() {
  if (typeof window === "undefined") return false;
  const role = getAdminPanelRole();
  return ROLES_WITH_DAILY_TARGETS.has(role);
}

function formatDateLabel(dateKey) {
  if (!dateKey) return "Today";
  const d = new Date(`${dateKey}T12:00:00`);
  if (Number.isNaN(d.getTime())) return dateKey;
  return d.toLocaleDateString(undefined, {
    weekday: "short",
    month: "short",
    day: "numeric",
  });
}

function ChatTargetLine({ targets }) {
  if (!targets?.targetsSet) {
    return <span className="text-gray-400 italic">Not set</span>;
  }
  return (
    <span>
      {targets.chatMessages} chats · {targets.emailsAdded} emails · {targets.socialResponses} social
      {targets.randomRequests > 0 ? ` · ${targets.randomRequests} random` : ""}
    </span>
  );
}

function CsrProgressBar({ done, target }) {
  if (!target || target <= 0) return null;
  const pct = Math.min(100, Math.round((done / target) * 100));
  return (
    <div className="mt-1.5">
      <div className="h-1.5 w-full rounded-full bg-gray-200 overflow-hidden">
        <div
          className={`h-full rounded-full transition-all ${pct >= 100 ? "bg-green-500" : "bg-[#FF2D38]"}`}
          style={{ width: `${pct}%` }}
        />
      </div>
      <p className="text-[10px] text-gray-500 mt-0.5">{pct}% of daily call target</p>
    </div>
  );
}

function CsrTargetLine({ targets, actuals, showProgress = false }) {
  const callsDone = Number(actuals?.callsCount) || 0;
  const premiumDone = Number(actuals?.premiumCount) || 0;

  if (!targets?.targetsSet) {
    if (callsDone > 0 || premiumDone > 0) {
      return (
        <span>
          {callsDone > 0 ? `${callsDone} call${callsDone === 1 ? "" : "s"} logged` : null}
          {callsDone > 0 && premiumDone > 0 ? " · " : null}
          {premiumDone > 0 ? `${premiumDone} premium` : null}
          <span className="text-gray-400"> · target not set</span>
        </span>
      );
    }
    return <span className="text-gray-400 italic">Not set</span>;
  }

  const callsTarget = Number(targets.callsTarget) || 0;
  const premiumTarget = Number(targets.premiumTarget) || 0;
  const callsMet = callsTarget > 0 && callsDone >= callsTarget;
  const premiumMet = premiumTarget > 0 && premiumDone >= premiumTarget;

  if (targets.targetMode === "calls_only") {
    return (
      <>
        <span>
          <span className={callsMet ? "text-green-600 font-semibold" : ""}>
            {callsDone}/{callsTarget} calls
          </span>
          {callsMet ? <span className="text-green-600"> · achieved</span> : null}
        </span>
        {showProgress ? <CsrProgressBar done={callsDone} target={callsTarget} /> : null}
      </>
    );
  }

  return (
    <>
      <span>
        <span className={callsMet ? "text-green-600 font-semibold" : ""}>
          {callsDone}/{callsTarget} calls
        </span>
        {" · "}
        <span className={premiumMet ? "text-green-600 font-semibold" : ""}>
          {premiumDone}/{premiumTarget} premium
        </span>
        {callsMet && (premiumTarget === 0 || premiumMet) ? (
          <span className="text-green-600"> · achieved</span>
        ) : null}
      </span>
      {showProgress ? <CsrProgressBar done={callsDone} target={callsTarget} /> : null}
    </>
  );
}

function TeamSection({ title, rows, renderLine }) {
  if (!rows?.length) return null;
  return (
    <div className="mt-3">
      <p className="text-[11px] font-bold uppercase tracking-wide text-gray-500 mb-2">{title}</p>
      <div className="space-y-2">
        {rows.map((row) => (
          <div
            key={row.agentId}
            className="rounded-md border border-gray-100 bg-gray-50 px-2.5 py-2"
          >
            <p className="text-xs font-semibold text-gray-800 truncate">
              {row.agentName || `Agent #${row.agentId}`}
            </p>
            <div className="text-[11px] text-gray-600 mt-0.5">{renderLine(row)}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function MineBlock({ title, children }) {
  return (
    <div className="mt-2 rounded-md border border-rose-100 bg-rose-50/40 px-2.5 py-2">
      <p className="text-[11px] font-semibold text-gray-700 mb-0.5">{title}</p>
      <div className="text-[11px] text-gray-600">{children}</div>
    </div>
  );
}

function ChatSupportSection({ section }) {
  if (!section) return null;
  const { canViewTeam, mine, team } = section;
  const teamOthers = (team || []).filter((row) => !row.isTeamLeadSelf);

  return (
    <div>
      <p className="text-[11px] font-bold uppercase tracking-wide text-[#FF2D38]">Chat Support</p>
      {mine ? (
        <MineBlock title={`Your targets${mine.agentName ? ` — ${mine.agentName}` : ""}`}>
          <ChatTargetLine targets={mine.targets} />
        </MineBlock>
      ) : null}
      {canViewTeam && teamOthers.length > 0 ? (
        <TeamSection
          title="Team targets"
          rows={teamOthers}
          renderLine={(row) => <ChatTargetLine targets={row.targets} />}
        />
      ) : null}
    </div>
  );
}

function CsrOutreachSection({ section }) {
  if (!section) return null;
  const { canViewTeam, mine, team } = section;
  const teamOthers = (team || []).filter((row) => !row.isTeamLeadSelf);

  return (
    <div>
      <p className="text-[11px] font-bold uppercase tracking-wide text-[#FF2D38]">CSR Outreach</p>
      {mine ? (
        <MineBlock title={`Your targets${mine.agentName ? ` — ${mine.agentName}` : ""}`}>
          <CsrTargetLine targets={mine.targets} actuals={mine.actuals} showProgress />
        </MineBlock>
      ) : null}
      {canViewTeam && teamOthers.length > 0 ? (
        <TeamSection
          title="Team targets"
          rows={teamOthers}
          renderLine={(row) => (
            <CsrTargetLine targets={row.targets} actuals={row.actuals} showProgress />
          )}
        />
      ) : null}
    </div>
  );
}

function shouldShowDropdown() {
  return canSeeDailyTargetsHeader();
}

function buildHeaderSummary(data, loading, error) {
  const csrMine = data?.csr?.mine;
  const chatMine = data?.chatSupport?.mine;

  if (csrMine) {
    const done = Number(csrMine.actuals?.callsCount) || 0;
    const target = Number(csrMine.targets?.callsTarget) || 0;
    if (csrMine.targets?.targetsSet && target > 0) {
      return { label: `${done}/${target} calls`, met: done >= target };
    }
    if (done > 0) {
      return { label: `${done} call${done === 1 ? "" : "s"} today`, met: false };
    }
  }

  if (chatMine?.targets?.targetsSet) {
    const t = chatMine.targets;
    return {
      label: `${t.chatMessages} chats · ${t.emailsAdded} emails`,
      met: false,
    };
  }

  if (loading && !data) return { label: "Loading…", met: false };
  if (error) return { label: "Tap to retry", met: false };
  return { label: "Daily targets", met: false };
}

export default function DailyTargetsDropdown() {
  const enabled = shouldShowDropdown();
  const { data, loading, error, refresh } = useHeaderDailyTargets(enabled);

  if (!enabled) return null;

  const summary = buildHeaderSummary(data, loading, error);
  const hasChat = Boolean(data?.chatSupport);
  const hasCsr = Boolean(data?.csr);

  return (
    <Popover className="relative">
      <PopoverButton
        type="button"
        className={`flex items-center gap-2 rounded-lg border px-3 py-1.5 text-xs font-semibold shadow-sm outline-none transition-colors ${
          summary.met
            ? "border-green-200 bg-green-50 text-green-800 hover:bg-green-100"
            : "border-rose-200 bg-rose-50 text-gray-800 hover:bg-rose-100"
        }`}
        title="Today's daily targets"
      >
        <span className="text-[10px] uppercase tracking-wide font-bold text-[#FF2D38]">
          Target
        </span>
        <span className={`text-sm font-bold ${summary.met ? "text-green-700" : "text-gray-900"}`}>
          {summary.label}
        </span>
        <HiChevronDown className="h-4 w-4 text-gray-500 shrink-0" />
      </PopoverButton>
      <Transition
        as={Fragment}
        enter="transition ease-out duration-100"
        enterFrom="transform scale-95 opacity-0"
        enterTo="transform scale-100 opacity-100"
        leave="transition ease-in duration-75"
        leaveFrom="transform scale-100 opacity-100"
        leaveTo="transform scale-95 opacity-0"
      >
        <PopoverPanel className="absolute right-0 z-50 mt-2 w-[min(92vw,320px)] rounded-lg border border-gray-200 bg-white p-3 shadow-lg">
          <div className="flex items-center justify-between gap-2 border-b border-gray-100 pb-2 mb-2">
            <p className="text-sm font-bold text-gray-900">Daily targets</p>
            <span className="text-[10px] font-medium text-gray-500">
              {formatDateLabel(data?.targetDate)}
            </span>
          </div>

          {loading && !data ? (
            <p className="text-xs text-gray-500 py-2">Loading targets…</p>
          ) : null}

          {hasChat ? <ChatSupportSection section={data.chatSupport} /> : null}

          {hasCsr ? (
            <div className={hasChat ? "mt-3 pt-3 border-t border-gray-100" : ""}>
              <CsrOutreachSection section={data.csr} />
            </div>
          ) : null}

          {!hasChat && !hasCsr && !loading ? (
            <div className="py-2">
              <p className="text-xs text-gray-500">
                {error ? "Could not load targets." : "No targets set for today yet."}
              </p>
              <button
                type="button"
                onClick={() => refresh()}
                className="mt-2 text-xs font-semibold text-[#FF2D38] hover:underline"
              >
                Refresh
              </button>
            </div>
          ) : null}
        </PopoverPanel>
      </Transition>
    </Popover>
  );
}
